import { useReducer, useEffect, useRef, useMemo } from 'react';

/**
 * Action types for forecast selection reducer
 */
const FORECAST_SELECTION_ACTIONS = {
	SET_PRODUCT: 'SET_PRODUCT',
	SET_CATEGORY: 'SET_CATEGORY',
	SET_PRODUCT_ID: 'SET_PRODUCT_ID',
	SET_SERIES: 'SET_SERIES',
	RESET: 'RESET',
	RESET_SERIES: 'RESET_SERIES',
	SELECT_FIRST_PRODUCT: 'SELECT_FIRST_PRODUCT',
};

/**
 * Initial state for forecast selection
 */
const initialState = {
	product: {
		category: '',
		category_description: '',
		label: '',
		value: ''
	},
	category: '',
	productID: '',
	series: '',
};

/**
 * Reducer for forecast selection state
 * Handles all state transitions in a predictable way
 */
function forecastSelectionReducer(state, action) {
	switch (action.type) {
		case FORECAST_SELECTION_ACTIONS.SET_PRODUCT:
			return {
				...state,
				product: action.payload,
			};
		
		case FORECAST_SELECTION_ACTIONS.SET_CATEGORY:
			return {
				...state,
				category: action.payload,
			};
		
		case FORECAST_SELECTION_ACTIONS.SET_PRODUCT_ID:
			return {
				...state,
				productID: action.payload,
			};
		
		case FORECAST_SELECTION_ACTIONS.SET_SERIES:
			return {
				...state,
				series: action.payload,
			};
		
		case FORECAST_SELECTION_ACTIONS.RESET:
			return initialState;
		
		case FORECAST_SELECTION_ACTIONS.RESET_SERIES:
			return {
				...state,
				series: '',
			};
		
		case FORECAST_SELECTION_ACTIONS.SELECT_FIRST_PRODUCT:
			const { product, category, productID, series } = action.payload;
			return {
				product,
				category,
				productID,
				series,
			};
		
		default:
			return state;
	}
}

/**
 * Custom hook for managing forecast selection state with auto-selection logic
 * 
 * This hook manages the selection of forecast products, categories, and series,
 * with automatic selection of the first available product when:
 * - Forecast type changes
 * - Products are loaded for the first time
 * - Current selection becomes invalid
 * 
 * @param {Array} uniqueProducts - Array of unique products available for selection
 * @param {Array} forecastedProducts - Full array of forecasted products (for category lookup)
 * @param {string} forecastType - Current forecast type value
 * @returns {Object} Selection state and dispatch functions
 */
export function useForecastSelection(uniqueProducts, forecastedProducts, forecastType) {
	const [state, dispatch] = useReducer(forecastSelectionReducer, initialState);
	
	// Track previous forecast type to detect changes
	const prevForecastTypeRef = useRef(forecastType);
	const lastSelectedProductRef = useRef('');
	const isInitialMount = useRef(true);

	// Memoize product validity check to avoid stale closure
	// This memo updates when state.productID or uniqueProducts change
	const currentProductValidMemo = useMemo(() => {
		if (!state.productID || !uniqueProducts || uniqueProducts.length === 0) {
			return false;
		}
		return uniqueProducts.some(p => p.value === state.productID);
	}, [state.productID, uniqueProducts]);

	// Auto-select first product when forecast type changes or products are loaded
	// This effect depends on the memoized validity check to avoid stale closures
	useEffect(() => {
		const forecastTypeChanged = prevForecastTypeRef.current !== forecastType;
		
		// Update ref when forecast type changes
		if (forecastTypeChanged) {
			prevForecastTypeRef.current = forecastType;
			lastSelectedProductRef.current = '';
			dispatch({ type: FORECAST_SELECTION_ACTIONS.RESET_SERIES });
		}

		if (uniqueProducts && uniqueProducts.length > 0 && forecastType) {
			// Use memoized validity check instead of reading state.productID directly
			// This ensures we always have the current value
			
			// Determine if we should select the first product
			const shouldSelectFirst = forecastTypeChanged || 
				lastSelectedProductRef.current === '' || 
				(isInitialMount.current && !state.productID) ||
				!currentProductValidMemo;
			
			if (shouldSelectFirst) {
				// Set the first product as selected
				const firstProduct = uniqueProducts[0];
				// Find the product in the original list to get the correct category
				const originalProduct = forecastedProducts.find(p => p.value === firstProduct.value);
				const productCategory = originalProduct?.category || firstProduct.category || '';
				
				// Auto-select first series if available and not cost type
				const series = (forecastType !== 'cost' && firstProduct.series) 
					? firstProduct.series 
					: '';
				
				// Dispatch a single action to update all related state
				dispatch({
					type: FORECAST_SELECTION_ACTIONS.SELECT_FIRST_PRODUCT,
					payload: {
						product: {
							category: productCategory,
							category_description: productCategory,
							label: firstProduct.label,
							value: firstProduct.value
						},
						category: productCategory,
						productID: firstProduct.value,
						series,
					}
				});
				
				lastSelectedProductRef.current = firstProduct.value;
			}
		} else if (forecastTypeChanged || (isInitialMount.current && forecastType && uniqueProducts.length === 0)) {
			// Reset selection when forecast type changes but no products available yet
			// Or on initial mount when forecast type is set but products haven't loaded
			dispatch({ type: FORECAST_SELECTION_ACTIONS.RESET });
			lastSelectedProductRef.current = '';
		}

		// Mark initial mount as complete after first run
		if (isInitialMount.current) {
			isInitialMount.current = false;
		}
	}, [uniqueProducts, forecastType, forecastedProducts, currentProductValidMemo, state.productID]); // Include memoized validity and state.productID for initial mount check

	// Manual selection handlers
	const setProduct = (product, category, productID, series = '') => {
		dispatch({
			type: FORECAST_SELECTION_ACTIONS.SELECT_FIRST_PRODUCT,
			payload: {
				product: {
					category: category || product.category || '',
					category_description: category || product.category || '',
					label: product.label,
					value: product.value
				},
				category: category || product.category || '',
				productID: productID || product.value,
				series,
			}
		});
		lastSelectedProductRef.current = productID || product.value;
	};

	const setSeries = (series) => {
		dispatch({ type: FORECAST_SELECTION_ACTIONS.SET_SERIES, payload: series });
	};

	return {
		selectedForecastProduct: state.product,
		selectedForecastCategory: state.category,
		selectedForecastProductID: state.productID,
		selectedForecastSeries: state.series,
		setProduct,
		setSeries,
	};
}

